import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useMemo } from "react";
import { Box, Button, Collapse, Divider, Flex, Grid, HStack, Icon, IconButton, Image, Spinner, Text, VStack, } from "@chakra-ui/react";
import { AddIcon, ArrowForwardIcon, MinusIcon } from "@chakra-ui/icons";
import { usePubEventsByDate } from "../../hooks/usePubEvents";
import { isTimeSlotWithinBuffer } from "../../helpers/bookingAvailability";
import { eventTypeColors } from "../../shared-utils/values";
import { FaLongArrowAltRight } from "react-icons/fa";
const SLOT_INTERVAL_MINUTES = 60;
const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};
const DayEventBookingContent = ({ pubId, dayEventUuid, onNavigate, pubColors, }) => {
    const defaultColors = {
        bg: "#16171C",
        text: "#FFFFFF",
        primary: "#8155FE",
        secondary: "#A383FF",
        card_bg: "#202020",
        card_border: "#2B2B2B",
        icon_color: "white",
    };
    const colors = { ...defaultColors, ...pubColors };
    const getPeriodFromCurrentTime = () => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12)
            return "morning";
        if (hour >= 12 && hour < 17)
            return "afternoon";
        return "evening";
    };
    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [selectedPeriod, setSelectedPeriod] = useState(getPeriodFromCurrentTime);
    const [selectedDayEventSlot, setSelectedDayEventSlot] = useState(null);
    const [maleCount, setMaleCount] = useState(0);
    const [femaleCount, setFemaleCount] = useState(0);
    const scrollContainerRef = useRef(null);
    const todayRef = useRef(null);
    const dateRefs = useRef({});
    const eventCardRefs = useRef([]);
    const eventsScrollContainerRef = useRef(null);
    const morningRef = useRef(null);
    const afternoonRef = useRef(null);
    const eveningRef = useRef(null);
    const lateNightRef = useRef(null);
    const slotsContainerRef = useRef(null);
    const isScrollingByTab = useRef(false);
    const selectedDateString = formatDateForAPI(selectedDate);
    const { data: eventsData, isLoading } = usePubEventsByDate(pubId, selectedDateString);
    const allEvents = useMemo(() => {
        var _a;
        if (!((_a = eventsData === null || eventsData === void 0 ? void 0 : eventsData.data) === null || _a === void 0 ? void 0 : _a.events) || !Array.isArray(eventsData.data.events)) {
            return [];
        }
        return eventsData.data.events;
    }, [eventsData]);
    const nonDayEvents = useMemo(() => {
        return allEvents.filter((event) => !event.is_day_event);
    }, [allEvents]);
    const dayEvent = useMemo(() => {
        var _a;
        return (_a = allEvents.find((event) => event.is_day_event === true)) !== null && _a !== void 0 ? _a : null;
    }, [allEvents]);
    const today = useRef(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }).current();
    const dates = useRef(() => {
        const result = [];
        for (let i = 0; i < 30; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            result.push(date);
        }
        return result;
    }).current();
    const formatDayName = (date) => date.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
    const formatMonth = (date) => date.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
    const isToday = (date) => date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
    const isSelected = (date) => date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();
    const handleDateClick = (date) => {
        setSelectedDate(date);
        setSelectedDayEventSlot(null);
    };
    const parseSlotTime = (time) => {
        if (!time)
            return null;
        if (Array.isArray(time)) {
            const [y, m, d, h, min] = time;
            return new Date(y, m - 1, d, h, min);
        }
        if (typeof time === "string") {
            const d = new Date(time);
            return isNaN(d.getTime()) ? null : d;
        }
        return null;
    };
    const formatSlotLabel = (hour, minute) => {
        const displayHour = hour % 12 === 0 ? 12 : hour % 12;
        const ampm = hour < 12 ? "AM" : "PM";
        return `${String(displayHour).padStart(2, "0")}:${String(minute).padStart(2, "0")}${ampm}`;
    };
    const getPeriodForHour = (hour) => {
        if (hour < 12)
            return "morning";
        if (hour < 17)
            return "afternoon";
        return "evening";
    };
    const formatDisplayDate = (date) => date.toLocaleDateString("en-US", {
        weekday: "short",
        day: "2-digit",
        month: "short",
    });
    const applyTimeToSelectedDate = (source) => {
        const normalizedDate = new Date(selectedDate);
        normalizedDate.setHours(source.getHours(), source.getMinutes(), source.getSeconds(), source.getMilliseconds());
        return normalizedDate;
    };
    const getEventSlots = (event) => {
        const directSlots = Array.isArray(event.direct_slots)
            ? event.direct_slots
            : Array.isArray(event.slots)
                ? event.slots
                : [];
        const subEventSlots = Array.isArray(event.sub_events)
            ? event.sub_events.flatMap((subEvent) => Array.isArray(subEvent.slots) ? subEvent.slots : [])
            : [];
        return [...directSlots, ...subEventSlots];
    };
    const getEventSlotRange = (event) => {
        const slots = getEventSlots(event);
        let slotStart = null;
        let slotEnd = null;
        for (const slot of slots) {
            const currentStart = parseSlotTime(slot.start_time);
            const currentEnd = parseSlotTime(slot.end_time);
            if (currentStart && (!slotStart || currentStart < slotStart)) {
                slotStart = currentStart;
            }
            if (currentEnd && (!slotEnd || currentEnd > slotEnd)) {
                slotEnd = currentEnd;
            }
        }
        if (!slotStart || !slotEnd || slotStart >= slotEnd)
            return null;
        slotStart.setSeconds(0, 0);
        slotEnd.setSeconds(0, 0);
        return { start: slotStart, end: slotEnd };
    };
    const isEventLive = (event) => {
        const range = getEventSlotRange(event);
        if (!range)
            return false;
        const now = new Date();
        return now >= range.start && now <= range.end;
    };
    const isEventEnded = (event) => {
        const range = getEventSlotRange(event);
        if (!range)
            return true;
        return range.end < new Date();
    };
    const visibleNonDayEvents = useMemo(() => {
        return nonDayEvents.filter((event) => !isEventEnded(event));
    }, [nonDayEvents]);
    /** Events that start on the selected date (excludes overnight continuation from previous day) */
    const displayedNonDayEvents = useMemo(() => {
        return visibleNonDayEvents.filter((event) => {
            const slotRange = getEventSlotRange(event);
            if (!slotRange)
                return true;
            const startDay = new Date(slotRange.start.getFullYear(), slotRange.start.getMonth(), slotRange.start.getDate());
            const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
            return startDay.getTime() >= selectedDay.getTime();
        });
    }, [visibleNonDayEvents, selectedDate]);
    const liveEventIndex = useMemo(() => {
        return displayedNonDayEvents.findIndex(isEventLive);
    }, [displayedNonDayEvents]);
    useEffect(() => {
        if (displayedNonDayEvents.length <= 1 || liveEventIndex < 0)
            return;
        const container = eventsScrollContainerRef.current;
        const cardEl = eventCardRefs.current[liveEventIndex];
        if (!container || !cardEl)
            return;
        const scrollToLive = () => {
            const cardLeft = cardEl.offsetLeft;
            const cardWidth = cardEl.offsetWidth;
            const containerWidth = container.clientWidth;
            container.scrollLeft = cardLeft - containerWidth / 2 + cardWidth / 2;
        };
        const t = setTimeout(scrollToLive, 100);
        return () => clearTimeout(t);
    }, [displayedNonDayEvents.length, liveEventIndex]);
    const getNonDayEventBookableWindows = (event) => {
        const slots = getEventSlots(event);
        const bufferMinutes = (event === null || event === void 0 ? void 0 : event.buffer_time_minutes) || 0;
        return slots
            .map((slot) => {
            const start = parseSlotTime(slot.start_time);
            const end = parseSlotTime(slot.end_time);
            if (!start || !end || start >= end)
                return null;
            if (!isTimeSlotWithinBuffer(start, bufferMinutes)) {
                const now = new Date();
                const minimumBookableTime = new Date(now.getTime() + bufferMinutes * 60 * 1000);
                if (minimumBookableTime >= end)
                    return null;
                start.setTime(minimumBookableTime.getTime());
            }
            start.setSeconds(0, 0);
            end.setSeconds(0, 0);
            return { start, end };
        })
            .filter((window) => !!window);
    };
    const getEventBookableWindows = (event) => {
        var _a, _b;
        const bookingStart = parseSlotTime(event.booking_start_time);
        const bookingEnd = parseSlotTime(event.booking_end_time);
        const slotRange = getEventSlotRange(event);
        if (!event.is_day_event) {
            const slotWindows = getNonDayEventBookableWindows(event);
            if (slotWindows.length > 0)
                return slotWindows;
            if (slotRange) {
                const bufferMinutes = (event === null || event === void 0 ? void 0 : event.buffer_time_minutes) || 0;
                if (!isTimeSlotWithinBuffer(slotRange.start, bufferMinutes)) {
                    const now = new Date();
                    const minimumBookableTime = new Date(now.getTime() + bufferMinutes * 60 * 1000);
                    if (minimumBookableTime >= slotRange.end)
                        return [];
                    slotRange.start = new Date(minimumBookableTime);
                    slotRange.start.setSeconds(0, 0);
                }
                return [slotRange];
            }
        }
        let start = (_a = bookingStart !== null && bookingStart !== void 0 ? bookingStart : slotRange === null || slotRange === void 0 ? void 0 : slotRange.start) !== null && _a !== void 0 ? _a : null;
        let end = (_b = bookingEnd !== null && bookingEnd !== void 0 ? bookingEnd : slotRange === null || slotRange === void 0 ? void 0 : slotRange.end) !== null && _b !== void 0 ? _b : null;
        if (!start || !end || start >= end)
            return [];
        start = applyTimeToSelectedDate(start);
        end = applyTimeToSelectedDate(end);
        if (end <= start) {
            end.setDate(end.getDate() + 1);
        }
        start.setSeconds(0, 0);
        end.setSeconds(0, 0);
        return [{ start, end }];
    };
    const bookableWindows = useMemo(() => {
        const dayStart = new Date(selectedDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);
        const rawWindows = allEvents
            .flatMap((event) => getEventBookableWindows(event))
            .filter((window) => window.end > dayStart && window.start < dayEnd);
        const baseWindows = rawWindows.map((window) => ({
            start: window.start < dayStart ? new Date(dayStart) : window.start,
            end: window.end > dayEnd ? new Date(dayEnd) : window.end,
        }));
        // When viewing the event's start date, also include the segment of any
        // window that spans past midnight into the next day so that late-night
        // slots (e.g. 12–2 AM) are also visible on the start date.
        const overflowWindows = [];
        const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
        rawWindows.forEach((window) => {
            if (window.end <= dayEnd)
                return;
            const windowStartDay = new Date(window.start.getFullYear(), window.start.getMonth(), window.start.getDate());
            if (windowStartDay.getTime() !== selectedDay.getTime())
                return;
            overflowWindows.push({
                start: new Date(dayEnd),
                end: window.end,
            });
        });
        return [...baseWindows, ...overflowWindows];
    }, [allEvents, selectedDate]);
    const isSlotWithinAnyBookableWindow = (hour, minute) => {
        if (!bookableWindows.length)
            return false;
        return bookableWindows.some((window) => {
            const base = new Date(window.start);
            const checkTime = new Date(base);
            checkTime.setHours(hour, minute, 0, 0);
            return checkTime >= window.start && checkTime < window.end;
        });
    };
    const availableSlots = useMemo(() => {
        const slots = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += SLOT_INTERVAL_MINUTES) {
                if (!isSlotWithinAnyBookableWindow(hour, minute))
                    continue;
                slots.push({
                    hour,
                    minute,
                    label: formatSlotLabel(hour, minute),
                });
            }
        }
        return slots;
    }, [bookableWindows, selectedDate]);
    /** Hours that are part of an overflow window (next-day segment) when viewing the start date - e.g. 0, 1 for 12AM–2AM. */
    const overflowSlotHours = useMemo(() => {
        const dayStart = new Date(selectedDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);
        const hours = new Set();
        bookableWindows.forEach((window) => {
            if (window.start.getTime() !== dayEnd.getTime())
                return;
            const endHour = window.end.getHours();
            const endMin = window.end.getMinutes();
            for (let h = 0; h < 24; h++) {
                if (h < endHour || (h === endHour && endMin > 0))
                    hours.add(h);
            }
        });
        return hours;
    }, [bookableWindows, selectedDate]);
    const slotsByPeriod = useMemo(() => {
        return availableSlots.reduce((acc, slot) => {
            const period = overflowSlotHours.has(slot.hour)
                ? "lateNight"
                : getPeriodForHour(slot.hour);
            acc[period].push(slot);
            return acc;
        }, {
            morning: [],
            afternoon: [],
            evening: [],
            lateNight: [],
        });
    }, [availableSlots, overflowSlotHours]);
    const orderedPeriodKeys = [
        "morning",
        "afternoon",
        "evening",
        "lateNight",
    ];
    const periodLabels = {
        morning: "Morning",
        afternoon: "Afternoon",
        evening: "Evening",
        lateNight: "Late night",
    };
    const visiblePeriodKeys = useMemo(() => orderedPeriodKeys.filter((period) => slotsByPeriod[period].length > 0), [slotsByPeriod]);
    const getEventAtSlot = (hour, minute) => {
        if (!nonDayEvents.length)
            return null;
        // For late-night overflow hours (0–X that actually belong to the *next* calendar day
        // of an event that started on the selected date), align the check date with that
        // overflow segment so the slot maps back to the correct non-day event.
        const baseDate = new Date(selectedDate);
        if (overflowSlotHours.has(hour)) {
            baseDate.setDate(baseDate.getDate() + 1);
        }
        for (const event of nonDayEvents) {
            const bookableWindows = getEventBookableWindows(event);
            if (!bookableWindows.length)
                continue;
            const checkTime = new Date(baseDate);
            checkTime.setHours(hour, minute, 0, 0);
            if (bookableWindows.some((window) => checkTime >= window.start && checkTime < window.end)) {
                return { uuid: event.uuid };
            }
        }
        return null;
    };
    /** Returns the day name (e.g. "Fri") if this event started on a previous calendar day and continues into selectedDate; null otherwise. */
    const getOvernightContinuationDayName = (event) => {
        const slotRange = getEventSlotRange(event);
        if (!slotRange)
            return null;
        const startDay = new Date(slotRange.start.getFullYear(), slotRange.start.getMonth(), slotRange.start.getDate());
        const selectedDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
        if (startDay.getTime() >= selectedDay.getTime())
            return null;
        return formatDayName(slotRange.start);
    };
    const getDayEventUuid = () => {
        return (dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.uuid) || dayEventUuid;
    };
    const getDayEventSlotId = () => {
        var _a;
        if (!dayEvent || !selectedDayEventSlot)
            return null;
        const slots = getEventSlots(dayEvent);
        if (!Array.isArray(slots) || slots.length === 0)
            return null;
        // Build a Date for the selected slot time on the selectedDate
        const selectedDateTime = new Date(selectedDate);
        selectedDateTime.setHours(selectedDayEventSlot.hour, selectedDayEventSlot.minute, 0, 0);
        for (const slot of slots) {
            const startRaw = parseSlotTime(slot.start_time);
            const endRaw = parseSlotTime(slot.end_time);
            if (!startRaw || !endRaw)
                continue;
            let slotStart = applyTimeToSelectedDate(startRaw);
            let slotEnd = applyTimeToSelectedDate(endRaw);
            if (slotEnd <= slotStart) {
                slotEnd = new Date(slotEnd);
                slotEnd.setDate(slotEnd.getDate() + 1);
            }
            if (selectedDateTime >= slotStart && selectedDateTime < slotEnd) {
                const id = slot.id;
                return typeof id === "number" ? id : null;
            }
        }
        // Fallback to the first slot id if none matched the time precisely
        const fallbackId = (_a = slots[0]) === null || _a === void 0 ? void 0 : _a.id;
        return typeof fallbackId === "number" ? fallbackId : null;
    };
    const isSlotInPast = (hour, minute) => {
        // Derive the actual calendar date for this slot:
        // - normal hours belong to selectedDate
        // - overflow late-night hours (e.g. 0–1 AM of the next day) belong to selectedDate + 1
        const slotDate = new Date(selectedDate);
        if (overflowSlotHours.has(hour)) {
            slotDate.setDate(slotDate.getDate() + 1);
        }
        // Only treat a slot as "past" if it falls on *today*; future days are always allowed.
        if (!isToday(slotDate))
            return false;
        const now = new Date();
        const slotHour = hour;
        const slotMinute = minute;
        return (slotHour < now.getHours() ||
            (slotHour === now.getHours() && slotMinute <= now.getMinutes()));
    };
    const handleSlotClick = (hour, minute) => {
        if (isSlotInPast(hour, minute))
            return;
        const eventInfo = getEventAtSlot(hour, minute);
        if (eventInfo) {
            onNavigate(`/pub/detail/events/${pubId}?date=${selectedDateString}&id=${eventInfo.uuid}`);
        }
        else {
            setSelectedDayEventSlot({
                hour,
                minute,
                label: formatSlotLabel(hour, minute),
            });
        }
    };
    const handleDayEventBookingContinue = () => {
        const uuid = getDayEventUuid();
        const slotId = getDayEventSlotId();
        if (!uuid || !selectedDayEventSlot || !slotId)
            return;
        const timeStr = `${String(selectedDayEventSlot.hour).padStart(2, "0")}:${String(selectedDayEventSlot.minute).padStart(2, "0")}`;
        const formattedDateTime = `${selectedDateString}T${timeStr}:00`;
        const queryParams = new URLSearchParams({
            date: selectedDateString,
            dayTime: timeStr,
            slot: String(slotId),
            time: formattedDateTime,
            "male-count": String(maleCount),
            "female-count": String(femaleCount),
        });
        if ((dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.is_table_booking_enabled) &&
            !(dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.is_pass_booking_enabled)) {
            onNavigate(`/event/booking/${uuid}?tab=Tables&step=2&${queryParams.toString()}`);
            return;
        }
        if ((dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.is_pass_booking_enabled) &&
            !(dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.is_table_booking_enabled)) {
            const slot = dayEvent.direct_slots[0];
            onNavigate(`/event/booking/${uuid}?direct_slot=${slot.name}_${slot.id}&${queryParams.toString()}`);
            return;
        }
        onNavigate(`/event/booking/pax/${uuid}?${queryParams.toString()}`);
    };
    const morningSlots = slotsByPeriod.morning;
    const afternoonSlots = slotsByPeriod.afternoon;
    const eveningSlots = slotsByPeriod.evening;
    const lateNightSlots = slotsByPeriod.lateNight;
    const isDayEventPaxStep = !!selectedDayEventSlot;
    const totalPax = maleCount + femaleCount;
    const handleSlotsScroll = () => {
        if (isScrollingByTab.current)
            return;
        const container = slotsContainerRef.current;
        if (!container)
            return;
        if (!visiblePeriodKeys.length)
            return;
        const refMap = {
            morning: morningRef,
            afternoon: afternoonRef,
            evening: eveningRef,
            lateNight: lateNightRef,
        };
        // Check if scrolled to the very bottom
        const isAtBottom = Math.ceil(container.scrollHeight - container.scrollTop) <=
            container.clientHeight + 5;
        if (isAtBottom) {
            const lastPeriod = visiblePeriodKeys[visiblePeriodKeys.length - 1];
            if (selectedPeriod !== lastPeriod)
                setSelectedPeriod(lastPeriod);
            return;
        }
        // Container's scroll position + some offset for active tab threshold
        const scrollPos = container.scrollTop + 50;
        let activePeriod = visiblePeriodKeys[0];
        for (const period of visiblePeriodKeys) {
            const sectionRef = refMap[period].current;
            if (sectionRef && scrollPos >= sectionRef.offsetTop) {
                activePeriod = period;
            }
        }
        if (selectedPeriod !== activePeriod) {
            setSelectedPeriod(activePeriod);
        }
    };
    const scrollToPeriod = (period) => {
        setSelectedPeriod(period);
        isScrollingByTab.current = true;
        const refMap = {
            morning: morningRef,
            afternoon: afternoonRef,
            evening: eveningRef,
            lateNight: lateNightRef,
        };
        const targetRef = refMap[period];
        if (targetRef.current && slotsContainerRef.current) {
            // Offset by -10px or so for visual breathing room, or exactly offsetTop.
            slotsContainerRef.current.scrollTo({
                top: targetRef.current.offsetTop,
                behavior: "smooth",
            });
        }
        // Resume scroll spy after animation
        setTimeout(() => {
            isScrollingByTab.current = false;
        }, 600);
    };
    // Scroll to the currently active time period on load and when date/loading changes
    useEffect(() => {
        if (!visiblePeriodKeys.length)
            return;
        const fallbackPeriod = visiblePeriodKeys[0];
        if (isToday(selectedDate)) {
            setSelectedPeriod(fallbackPeriod);
            // Only scroll once slots are rendered (not loading); refs are not in DOM while spinner is shown
            if (!isLoading) {
                const t = setTimeout(() => scrollToPeriod(fallbackPeriod), 150);
                return () => clearTimeout(t);
            }
        }
        else {
            setSelectedPeriod(fallbackPeriod);
            if (!isLoading) {
                const t = setTimeout(() => scrollToPeriod(fallbackPeriod), 150);
                return () => clearTimeout(t);
            }
        }
    }, [selectedDate, isLoading, visiblePeriodKeys]);
    useEffect(() => {
        if (scrollContainerRef.current && selectedDate) {
            const dateKey = formatDateForAPI(selectedDate);
            const dateElement = dateRefs.current[dateKey];
            if (dateElement) {
                const container = scrollContainerRef.current;
                const containerRect = container.getBoundingClientRect();
                const elementRect = dateElement.getBoundingClientRect();
                const isVisible = elementRect.left >= containerRect.left &&
                    elementRect.right <= containerRect.right;
                if (!isVisible) {
                    dateElement.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                        inline: "center",
                    });
                }
            }
        }
    }, [selectedDate]);
    const renderSlot = (slot) => {
        const eventInfo = getEventAtSlot(slot.hour, slot.minute);
        const hasEvent = !!eventInfo;
        const isPast = isSlotInPast(slot.hour, slot.minute);
        const isSelectedDaySlot = !hasEvent &&
            (selectedDayEventSlot === null || selectedDayEventSlot === void 0 ? void 0 : selectedDayEventSlot.hour) === slot.hour &&
            (selectedDayEventSlot === null || selectedDayEventSlot === void 0 ? void 0 : selectedDayEventSlot.minute) === slot.minute;
        return (_jsxs(Box, { py: 3, px: 2, borderRadius: "8px", border: `1px solid ${isSelectedDaySlot ? colors.primary : colors.card_border}`, bg: isSelectedDaySlot ? `${colors.primary}20` : "transparent", cursor: isPast ? "not-allowed" : "pointer", opacity: isPast ? 0.35 : 1, onClick: () => handleSlotClick(slot.hour, slot.minute), transition: "all 0.2s", textAlign: "center", _hover: isPast
                ? {}
                : {
                    borderColor: colors.primary,
                    bg: `${colors.primary}20`,
                }, _active: isPast ? {} : { bg: colors.primary }, children: [_jsx(Text, { fontSize: "xs", fontWeight: "400", color: colors.text, children: slot.label }), hasEvent && !isPast && (_jsx(Text, { fontSize: "10px", fontWeight: "400", color: "#F49F59", mt: 0.5, children: "\u2022 EVENT" }))] }, slot.label));
    };
    const Counter = ({ label, value, onDecrease, onIncrease, }) => (_jsxs(Flex, { justify: "space-between", align: "center", w: "100%", children: [_jsx(Text, { fontSize: "14px", fontWeight: "400", color: colors.text, children: label }), _jsxs(HStack, { spacing: 3, children: [_jsx(IconButton, { "aria-label": `Decrease ${label}`, icon: _jsx(MinusIcon, { color: colors.text, boxSize: 3 }), size: "sm", variant: "ghost", border: `1px solid ${colors.card_border}`, borderRadius: "full", color: colors.text, onClick: onDecrease, _hover: { bg: `${colors.primary}15` } }), _jsx(Text, { color: colors.text, w: "16px", fontWeight: "700", textAlign: "center", children: value }), _jsx(IconButton, { "aria-label": `Increase ${label}`, icon: _jsx(AddIcon, { color: colors.text, boxSize: 3 }), size: "sm", variant: "ghost", border: `1px solid ${colors.card_border}`, borderRadius: "full", color: colors.text, onClick: onIncrease, _hover: { bg: `${colors.primary}15` } })] })] }));
    return (_jsxs(Box, { w: "100%", minH: "70vh", children: [_jsx(Flex, { align: "center", gap: 0, position: "relative", overflow: "visible", h: "100%", children: _jsx(Box, { ref: scrollContainerRef, overflowX: "auto", overflowY: "hidden", flex: 1, css: {
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                    }, children: _jsx(Flex, { gap: 2, w: "max-content", position: "relative", style: { isolation: "isolate" }, children: dates.map((date, index) => {
                            const dayName = formatDayName(date);
                            const dayNum = date.getDate();
                            const month = formatMonth(date);
                            const isTodayDate = isToday(date);
                            const isSelectedDate = isSelected(date);
                            const dateKey = formatDateForAPI(date);
                            return (_jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "center", bg: "transparent", border: "none", h: "fit-content", position: isTodayDate ? "sticky" : "static", left: isTodayDate ? "-1px" : "auto", zIndex: isTodayDate ? 10 : 1, py: 1, children: [_jsxs(Box, { ref: (el) => {
                                            if (isTodayDate && el) {
                                                todayRef.current = el;
                                            }
                                            dateRefs.current[dateKey] = el;
                                        }, flexShrink: 0, w: "56px", minW: "56px", h: "66px", borderRadius: isSelectedDate ? "8px" : isTodayDate ? "0" : "8px", bg: isSelectedDate
                                            ? colors.primary
                                            : isTodayDate
                                                ? "transparent"
                                                : colors.card_bg, border: isSelectedDate || isTodayDate
                                            ? "none"
                                            : `1px solid ${colors.card_border}`, display: "flex", flexDir: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.2s", onClick: () => handleDateClick(date), children: [_jsx(Text, { fontSize: "10px", fontWeight: "400", color: isSelectedDate ? "white" : colors.text, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.5px", children: dayName }), _jsx(Text, { fontSize: "sm", fontWeight: "700", color: isSelectedDate ? "white" : colors.text, mb: 0.5, children: dayNum }), _jsx(Text, { fontSize: "10px", fontWeight: "400", color: isSelectedDate ? "white" : colors.text, style: {
                                                    opacity: isTodayDate || isSelectedDate ? 1 : 0.6,
                                                }, textTransform: "uppercase", letterSpacing: "0.5px", children: isTodayDate ? "Today" : month })] }), isTodayDate && (_jsx(Box, { w: "1px", h: "70px", bg: colors.card_border || "#353535", ml: 2, zIndex: 10 }))] }, index));
                        }) }) }) }), _jsx(Box, { h: "1px", bg: colors.card_border, my: 4 }), displayedNonDayEvents.length > 0 && !isDayEventPaxStep && (_jsxs(Box, { mb: 6, h: "100%", children: [_jsx(Text, { fontSize: "xs", fontWeight: "500", color: colors.text, mb: 3, children: "Events" }), _jsx(Flex, { ref: eventsScrollContainerRef, flexDir: "row", gap: 3, overflowX: displayedNonDayEvents.length > 1 ? "auto" : "visible", overflowY: "hidden", pb: 2, css: {
                            "&::-webkit-scrollbar": { height: "6px" },
                            "&::-webkit-scrollbar-thumb": {
                                background: colors.card_border,
                                borderRadius: "3px",
                            },
                        }, flexWrap: "nowrap", w: "100%", children: displayedNonDayEvents.map((event, index) => {
                            var _a, _b;
                            const eventTypeColor = (eventTypeColors === null || eventTypeColors === void 0 ? void 0 : eventTypeColors[(_a = event.event_type) === null || _a === void 0 ? void 0 : _a.toLowerCase()]) ||
                                "linear-gradient(95.58deg, #A855F7 12.18%, #E879F9 86.93%)";
                            let timeStr = event.time || "";
                            if (!timeStr) {
                                const slots = (_b = event.direct_slots) !== null && _b !== void 0 ? _b : event.slots;
                                if (slots && slots.length > 0) {
                                    const firstSlot = slots[0];
                                    const lastSlot = slots[slots.length - 1];
                                    const startD = parseSlotTime(firstSlot.start_time);
                                    const endD = parseSlotTime(lastSlot.end_time);
                                    if (startD && endD) {
                                        const formatTime = (d) => {
                                            let h = d.getHours();
                                            const m = String(d.getMinutes()).padStart(2, "0");
                                            const ampm = h >= 12 ? "PM" : "AM";
                                            h = h % 12 || 12;
                                            return `${h}:${m} ${ampm}`;
                                        };
                                        timeStr = `${formatTime(startD)} - ${formatTime(endD)}`;
                                    }
                                }
                            }
                            return (_jsxs(Flex, { ref: (el) => {
                                    eventCardRefs.current[index] = el;
                                }, bg: colors.card_bg, border: `1px solid ${colors.card_border}`, borderRadius: "16px", p: 2, align: "center", justify: "space-between", cursor: "pointer", onClick: () => onNavigate(`/pub/detail/events/${pubId}?date=${selectedDateString}&id=${event.uuid}`), _hover: {
                                    borderColor: colors.primary,
                                    transform: "translateY(-2px)",
                                }, transition: "all 0.2s", boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", w: displayedNonDayEvents.length === 1 ? "100%" : undefined, minW: displayedNonDayEvents.length > 1 ? "280px" : undefined, flex: displayedNonDayEvents.length === 1 ? 1 : "0 0 auto", children: [_jsxs(Flex, { gap: 4, align: "center", flex: 1, minW: 0, children: [event.image ? (_jsx(Image, { src: event.image, w: "60px", h: "60px", borderRadius: "10px", objectFit: "cover", alt: event.event_name, flexShrink: 0 })) : (_jsx(Flex, { w: "60px", h: "60px", borderRadius: "10px", bg: "#2B2B2B", align: "center", justify: "center", flexShrink: 0, children: _jsx(Text, { fontSize: "24px", children: "\uD83C\uDF89" }) })), _jsxs(Box, { flex: 1, minW: 0, children: [_jsx(Text, { fontSize: "10px", fontWeight: "800", bgGradient: eventTypeColor, bgClip: "text", color: "transparent", w: "fit-content", mb: 1, textTransform: "uppercase", letterSpacing: "0.5px", children: event.event_type || "EVENT" }), _jsx(Text, { fontSize: "15px", fontWeight: "600", color: colors.text, lineHeight: "1.2", noOfLines: 1, mb: timeStr ? 1 : 0, children: event.event_name }), timeStr && (_jsx(Text, { fontSize: "12px", fontWeight: "500", color: colors.text, opacity: 0.6, children: timeStr }))] })] }), _jsx(Box, { ml: 3, w: "32px", h: "32px", borderRadius: "full", bg: `${colors.primary}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, children: _jsx(Icon, { as: ArrowForwardIcon, color: colors.primary, boxSize: 4 }) })] }, event.uuid));
                        }) })] })), !isDayEventPaxStep && (_jsx(Text, { fontSize: "xs", fontWeight: "400", color: colors.text, mb: 4, children: "Select Your Visiting Time" })), !isDayEventPaxStep && visiblePeriodKeys.length > 1 && (_jsx(Flex, { gap: 3, mb: 5, children: visiblePeriodKeys.map((period) => {
                    const isActive = selectedPeriod === period;
                    return (_jsx(Box, { px: 3, py: 1, borderRadius: "full", border: `1px solid ${isActive ? colors.primary : colors.card_border}`, bg: isActive ? `${colors.primary}15` : "transparent", color: isActive ? colors.primary : colors.text, cursor: "pointer", onClick: () => scrollToPeriod(period), transition: "all 0.2s", fontSize: "xs", fontWeight: "400", children: periodLabels[period] }, period));
                }) })), _jsx(Collapse, { in: !isDayEventPaxStep, animateOpacity: true, children: isLoading ? (_jsx(Flex, { justify: "center", py: 8, children: _jsx(Spinner, { color: colors.primary }) })) : (_jsxs(Box, { ref: slotsContainerRef, maxH: "400px", overflowY: "auto", onScroll: handleSlotsScroll, position: "relative", pr: 2, pb: "250px", css: {
                        "&::-webkit-scrollbar": {
                            width: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: colors.card_border,
                            borderRadius: "4px",
                        },
                    }, children: [visiblePeriodKeys.includes("morning") &&
                            morningSlots.length > 0 && (_jsxs(Box, { ref: morningRef, mb: 6, children: [_jsx(Text, { fontSize: "xs", fontWeight: "500", color: colors.text, mb: 3, children: "Morning" }), _jsx(Grid, { templateColumns: "repeat(3, 1fr)", gap: 3, children: morningSlots.map((slot) => renderSlot(slot)) })] })), visiblePeriodKeys.includes("afternoon") &&
                            afternoonSlots.length > 0 && (_jsxs(Box, { ref: afternoonRef, mb: 6, children: [_jsx(Text, { fontSize: "xs", fontWeight: "500", color: colors.text, mb: 3, children: "Afternoon" }), _jsx(Grid, { templateColumns: "repeat(3, 1fr)", gap: 3, children: afternoonSlots.map((slot) => renderSlot(slot)) })] })), visiblePeriodKeys.includes("evening") &&
                            eveningSlots.length > 0 && (_jsxs(Box, { ref: eveningRef, mb: lateNightSlots.length > 0 ? 6 : 2, children: [_jsx(Text, { fontSize: "xs", fontWeight: "500", color: colors.text, mb: 3, children: "Evening" }), _jsx(Grid, { templateColumns: "repeat(3, 1fr)", gap: 3, children: eveningSlots.map((slot) => renderSlot(slot)) })] })), visiblePeriodKeys.includes("lateNight") &&
                            lateNightSlots.length > 0 && (_jsxs(Box, { ref: lateNightRef, mb: 2, children: [_jsx(Text, { fontSize: "xs", fontWeight: "500", color: colors.text, mb: 3, children: "Late night" }), _jsx(Grid, { templateColumns: "repeat(3, 1fr)", gap: 3, children: lateNightSlots.map((slot) => renderSlot(slot)) })] })), visiblePeriodKeys.length === 0 && (_jsx(Text, { fontSize: "sm", color: colors.text, opacity: 0.7, children: "No booking slots available for this date." }))] })) }), _jsx(Collapse, { in: isDayEventPaxStep, animateOpacity: true, children: _jsxs(Box, { py: 2, h: "100%", pb: "96px", children: [_jsxs(VStack, { align: "stretch", spacing: 4, children: [_jsx(Box, { px: 3, py: 2.5, borderRadius: "12px", bg: `${colors.primary}12`, children: _jsxs(Flex, { align: "center", justify: "space-between", gap: 3, children: [_jsxs(Box, { children: [_jsx(Text, { fontSize: "10px", color: colors.text, opacity: 0.72, mb: 0.5, children: "Selected time" }), _jsxs(Text, { fontSize: "sm", fontWeight: "600", color: colors.text, children: [selectedDayEventSlot === null || selectedDayEventSlot === void 0 ? void 0 : selectedDayEventSlot.label, _jsxs(Text, { as: "span", fontSize: "xs", fontWeight: "400", opacity: 0.7, children: [" ", "\u2022 ", formatDisplayDate(selectedDate)] })] })] }), _jsx(Button, { size: "sm", variant: "ghost", color: colors.primary, minW: "auto", h: "28px", px: 2, fontSize: "xs", _hover: { bg: `${colors.primary}15` }, onClick: () => setSelectedDayEventSlot(null), children: "Change time" })] }) }), _jsx(Text, { fontSize: "sm", fontWeight: "600", color: colors.text, children: "How Many Are In?" }), _jsxs(VStack, { w: "100%", spacing: 3, align: "stretch", bg: colors.card_bg, border: `1px solid ${colors.card_border}`, borderRadius: "16px", p: 4, children: [_jsx(Counter, { label: "Male", value: maleCount, onDecrease: () => setMaleCount((value) => Math.max(0, value - 1)), onIncrease: () => setMaleCount((value) => value + 1) }), _jsx(Divider, { borderColor: colors.card_border }), _jsx(Counter, { label: "Female", value: femaleCount, onDecrease: () => setFemaleCount((value) => Math.max(0, value - 1)), onIncrease: () => setFemaleCount((value) => value + 1) })] })] }), _jsx(Box, { position: "fixed", bottom: 6, left: 0, right: 0, pt: 4, pb: 4, px: 4, zIndex: 20, children: _jsx(Button, { w: "100%", bg: colors.primary, color: "white", borderRadius: "14px", h: "48px", fontSize: "sm", fontWeight: "500", maxW: "500px", isDisabled: totalPax === 0, rightIcon: _jsx(FaLongArrowAltRight, { size: 20 }), _hover: { bg: colors.primary, opacity: 0.92 }, onClick: handleDayEventBookingContinue, children: "Continue" }) })] }) })] }));
};
export default DayEventBookingContent;
